import { T, useTranslate } from '@tolgee/react';
import { GetServerSideProps } from 'next';
import Image from 'next/image';
import styled from 'styled-components';

import FrontImage from '../../../assets/images/LandingPage/frontImg.png';
import theme from '../../../assets/theme';
import { CheckoutCanceledModal } from '../../../components/_molecules/CheckoutCanceledModal/CheckoutCanceledModal';
import PricingPageContent from '../../../components/_organisms/PricingPageContent';
import { NextPageWithLayout } from '../../../components/layouts';
import LandingPageLayout from '../../../components/layouts/LandingPageLayout';
import { useLocale } from '../../../hooks/useLocale';
import { getLocaleProps } from '../../../utils/localisationUtils';

interface LocalizedPricingProps {
  locale: string;
}

const HeaderContainer = styled.div<{ $hideBlobs?: boolean }>`
  position: relative;
  background-image: url('/static/bground.svg');
  background-color: ${theme.colors.green};
  background-size: cover;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  color: ${theme.colors.white};
  height: 250px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 20px;

  h1 {
    font-size: 36px;
    margin: 0;
    color: ${theme.colors.white};
  }

  p {
    font-size: 20px;
    font-weight: 400;
    color: ${theme.colors.white};
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    height: fit-content;
    padding: 20px 10px;
  }
`;

const ImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  overflow: hidden;
  position: relative !important;

  span {
    height: 100% !important;
    width: 100% !important;
    position: absolute !important;

    img {
      object-fit: contain !important;
      position: relative !important;
      height: 100% !important;
      width: 100% !important;
    }
  }

  @media (max-width: 600px) {
    display: none;
  }
`;

const CustomHeader = () => {
  return (
    <HeaderContainer>
      <div style={{ maxWidth: 500, margin: 'auto ' }}>
        <h1>
          <T keyName="Pricing" defaultValue="Pricing" />
        </h1>
        <p>
          <T
            keyName="Explore our pricing details for educators bringing curiosity into the classroom
          and save time, spark curiosity, and increase engagement with interactive lessons
          students love"
            defaultValue="Explore our pricing details for educators bringing curiosity into the classroom
          and save time, spark curiosity, and increase engagement with interactive lessons
          students love."
          />
        </p>
      </div>
      <ImageWrapper>
        <Image
          style={{ objectFit: 'contain' }}
          alt="the Curipod team"
          height={FrontImage.height}
          width={FrontImage.width}
          src={FrontImage.src}
        />
      </ImageWrapper>
    </HeaderContainer>
  );
};

const LocalizedPricing: NextPageWithLayout<LocalizedPricingProps> = ({ locale }) => {
  const { t } = useTranslate();
  useLocale();

  // Language is set via SSR prop on TolgeeProvider, no need to set it manually

  // Get localized metadata using Tolgee
  const title = t('meta.pricing.title', 'Curipod Pricing - Plans for Educators');
  const description = t(
    'meta.pricing.description',
    'Explore our pricing details for educators bringing curiosity into the classroom and save time, spark curiosity, and increase engagement with interactive lessons students love.',
  );
  // Paused until decision on product event
  // useEffect(() => {
  //   // Track pricing page views for target roles
  //   const targetRoles = [
  //     'Instructional/Tech Coach',
  //     'School Administrator',
  //     'District Administrator',
  //     'Purchasing Manager',
  //     'adminDistrict',
  //   ];

  //   const userRole = accessBadge?.user?.profileInfo?.role;
  //   const organizationId = accessBadge?.organization?.id;
  //   const email = accessBadge?.user?.email;
  //   const userId = accessBadge?.user?.id;

  //   if (userRole && targetRoles.includes(userRole) && organizationId && userId && email) {
  //     trackAttioEvent('admin_pricing_page_view', {
  //       organizationId,
  //       userRole,
  //       userId,
  //       email,
  //       firstName: accessBadge?.user?.firstName,
  //       lastName: accessBadge?.user?.lastName,
  //       locale, // Track which locale was viewed
  //     });
  //   }
  // }, [accessBadge, trackAttioEvent, locale]);

  return (
    <>
      <CheckoutCanceledModal />
      <LandingPageLayout
        title={title} // SSR-provided, available immediately
        subTitle={description} // SSR-provided, available immediately
        locale={locale}
        hideBlobs={false}
        disableHeadline={true}
        customHeader={<CustomHeader />}
      >
        <PricingPageContent />
      </LandingPageLayout>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  return getLocaleProps(context);
};

LocalizedPricing.getLayout = function getLayout(page) {
  return <>{page}</>;
};

export default LocalizedPricing;
